import { useEffect } from "react";
import { Field, isSubmitting } from "redux-form";
import amplitude from "amplitude-js";

import { Box, Stack, Typography } from "@braintrust/braintrust-ui-components";
import {
  CheckIcon,
  ErrorSVGIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { AuthContainer } from "@js/apps/auth/components/auth-container/auth-container";
import { signUpAttempt } from "@js/apps/common/actions";
import { useWithRecaptcha } from "@js/apps/common/hooks";
import { RouterLink } from "@js/components/link";
import { PhoneField } from "@js/forms/fields/phone";
import { useAppSelector } from "@js/hooks/redux";
import { useHideRequestLoader } from "@js/hooks/use-hide-request-loader";

import { ONBOARDING_FORM_ID } from "../../constants";
import { useNewClientSignUpInvitation } from "../../hooks/new-client-signup-invitation";
import { useNewFreelancerSignUpInvitation } from "../../hooks/use-new-freelancer-signup-invitation";
import { useRegisterFlow } from "../../hooks/use-register-flow";
import { FirstOrLastNameField } from "../fields/first-name-field";
import { OrganizationSizeField } from "../fields/organization-size-field";
import { SignUpEmailField } from "../fields/sign-up-email-field";
import { SignupPasswordField } from "../fields/signup-password-field";
import { SMSOptInField } from "../fields/sms-opt-in";
import { TOSField } from "../fields/tos-field";
import { onboardingFormValueSelector } from "../form-wrapper";
import type { OnboardingOrganizationFields } from "../organization/schema";
import { SignUpConfirmButton } from "../sign-up-confirm-button";

import { CreateAccountHeader } from "./create-account-header";

export const CreateAccountPage = () => {
  const { isPostPublicApplicationSignup, isClientInvitationSignup } =
    useRegisterFlow();
  const { newFreelancerInvitationData } = useNewFreelancerSignUpInvitation();
  const { isClientInvitation, newClientInvitationData } =
    useNewClientSignUpInvitation();

  const prefilledEmailFromInvitation =
    newClientInvitationData?.email || newFreelancerInvitationData?.email;
  const isClientSignup = isClientInvitation;

  useHideRequestLoader();
  useWithRecaptcha();

  useEffect(() => {
    signUpAttempt(amplitude.getInstance().getDeviceId()).catch(() => null);
  }, []);

  const _isSubmitting = useAppSelector((state) =>
    isSubmitting(ONBOARDING_FORM_ID)(state),
  );

  const organizationSize: OnboardingOrganizationFields["organization_size"] =
    useAppSelector((state) => {
      return onboardingFormValueSelector(state, "organization_size");
    });

  const smsOptIn: boolean | undefined = useAppSelector((state) =>
    onboardingFormValueSelector(state, "sms_opt_in"),
  );

  const isPhoneNumberRequired =
    (organizationSize !== ENUMS.OrganizationSize.LESS_THAN_10 &&
      organizationSize !== ENUMS.OrganizationSize.SELF_EMPLOYED) ||
    smsOptIn;

  const isClientSignupPage = isClientInvitationSignup || isClientSignup;
  return (
    <>
      <AuthContainer
        pageTitle="Sign up"
        isSignupPage
        isClientSignupPage={isClientInvitationSignup || isClientSignup}
      >
        <CreateAccountHeader />
        <TopSection />
        <Stack sx={{ mt: 4, gap: 3 }}>
          <Stack
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FirstOrLastNameField
              variant="first_name"
              autoFocus
              prefixWithLegal={!isClientSignupPage}
            />

            <FirstOrLastNameField
              variant="last_name"
              prefixWithLegal={!isClientSignupPage}
            />
          </Stack>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
            }}
          >
            {isClientSignup && (
              <Field
                name="organization_size"
                component={OrganizationSizeField}
              />
            )}

            <SignUpEmailField
              variant={isClientSignup ? "business" : "talent"}
              required
              disableWhenPrefilled={
                !!prefilledEmailFromInvitation || isPostPublicApplicationSignup
              }
            />
            {isClientSignup && (
              <Field
                variant="outlined"
                id="phone_number"
                name="phone_number"
                label={`Mobile number${isPhoneNumberRequired ? "*" : ""}`}
                component={PhoneField}
              />
            )}
          </Box>

          <SignupPasswordField />
          <TOSField />
          {isClientSignup && <SMSOptInField />}
          <SignUpConfirmButton isLoading={_isSubmitting} />
          <Typography
            component="p"
            color="secondary"
            size="small"
            sx={{ mt: 1, textAlign: "center" }}
          >
            Already a member?{" "}
            <Typography
              component="link"
              to={"/auth/login/"}
              size="small"
              RouterLink={RouterLink}
              color="dark-violet"
              fontWeight={500}
            >
              Sign in.
            </Typography>
          </Typography>
        </Stack>
      </AuthContainer>
    </>
  );
};

const defaultGreeting = "You’re almost there!";

const PostPublicFreelancerBidCreationTopSection = () => {
  return (
    <>
      <Typography
        component="p"
        variant="paragraph"
        size="medium"
        color="dark-violet"
        fontWeight={500}
        sx={{ mb: 1, fontSize: "11px !important", "--dot-size": "5px" }}
        className="dot-prefix uppercase--wide"
      >
        THANKS FOR APPLYING
      </Typography>

      <Typography
        component="h1"
        variant="title"
        fontWeight={500}
        size="small"
        sx={{ mb: 1.5 }}
      >
        Sign up
      </Typography>

      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          py: 1,
          px: 1.5,
          mb: 3,
          background: "var(--soft-violet)",
          border: "1px solid var(--medium-violet)",
          borderRadius: 2.5,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "28px",
            height: "28px",
            display: "grid",
            placeItems: "center",
            background: "var(--medium-violet)",
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <ErrorSVGIcon
            sx={{ color: "var(--dark-violet)", fontSize: "18px" }}
          />
        </Box>
        <Typography component="p" size="small">
          <Typography component="span" fontWeight={500} size="small">
            Your application won’t be submitted until your profile is complete.
          </Typography>{" "}
          It only takes a minute.
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <GreenCheckCircleIcon />
          <Typography component="p" fontWeight={500} size="small">
            Finish profile
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <GreenCheckCircleIcon />
          <Typography component="p" fontWeight={500} size="small">
            Track application
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <GreenCheckCircleIcon />
          <Typography component="p" fontWeight={500} size="small">
            Get matched & paid
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

const ClientInvitationTopSection = () => {
  const { newClientInvitationData } = useNewClientSignUpInvitation();

  const firstNameFromInvitation = newClientInvitationData?.first_name;
  const greeting = firstNameFromInvitation
    ? `Hi, ${firstNameFromInvitation}. Nice to meet you.`
    : defaultGreeting;

  return (
    <>
      <Typography
        component="h2"
        variant="title"
        fontWeight={500}
        size="small"
        sx={{ mb: 0.5 }}
      >
        {greeting}
      </Typography>
      <Typography
        component="h1"
        variant="paragraph"
        size="small"
        color="grey-2"
      >
        Create an employer account to start hiring Talent
      </Typography>
    </>
  );
};

const DefaultTopSection = () => {
  return (
    <>
      <Typography component="h1" variant="title" size="small" sx={{ mb: 0.5 }}>
        Create your account
      </Typography>
      <Typography
        component="h2"
        variant="paragraph"
        fontWeight={400}
        size="small"
        color="grey-2"
      >
        It takes about 30 seconds. We'll build the rest from your resume.
      </Typography>
    </>
  );
};

const TopSection = () => {
  const { isClientInvitationSignup, isPostPublicApplicationSignup } =
    useRegisterFlow();
  if (isClientInvitationSignup) {
    return <ClientInvitationTopSection />;
  }

  if (isPostPublicApplicationSignup) {
    return <PostPublicFreelancerBidCreationTopSection />;
  }

  return <DefaultTopSection />;
};

const GreenCheckCircleIcon = () => {
  return (
    <Box
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        backgroundColor: "var(--positive-3)",
      }}
    >
      <CheckIcon sx={{ color: "var(--positive-1)", fontSize: "14px" }} />
    </Box>
  );
};
